import {
  checkStrategyItemSelected,
  getChildren,
  getCreateItem,
  getFieldType,
  getItemValueType,
  getName,
} from "features/configuration/GeneralConfiguration/utils";
import {
  ConfigurationItem,
  useConfigurationItem,
} from "features/configuration/GeneralConfiguration/ConfigurationItem";
import Button from "common/Buttons/Button";
import ArrowLeftOutlined from "@ant-design/icons/ArrowLeftOutlined";
import PlusOutlined from "@ant-design/icons/PlusOutlined";
import React, { useContext, useEffect, useState } from "react";
import {
  ModalItemContext,
  StrategyItemContext,
} from "features/configuration/GeneralConfiguration/generalConfigurationContexts";
import {
  CUSTOM_STRATEGIES_ITEM_NAME,
  DCA_STRATEGIES_ITEM_NAME,
  DCA_STRATEGY_FIELD_TYPE_STRING,
  STRATEGY_FIELD_TYPE_STRING,
} from "features/configuration/common/constants";
import HeaderItem from "features/configuration/GeneralConfiguration/ConfigurationItem/HeaderItem";
import Link from "common/Typography/Link";
import { OrderTypes } from "@shared/features/trades/common/types";
import { getIsSectionHeader } from "@shared/features/configuration/utils";
import EmptyView from "common/Empty/EmptyView";
import { displayResultMessage } from "utils/misc";
import { sharedLib } from "@shared/services/shared.service";
import Modal from "common/Modal/Modal";
import { useSharedCustomStrategies } from "@shared/features/configuration/hooks";
import { EmptyStatesType } from "common/Icons/types.js";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types/index.js";

const DataRadioItem = ({ item, ...props }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const {
    itemValue,
    handleItemChange,
    itemChildren,
    checkItemModified,
    itemName,
  } = useConfigurationItem({ item });
  const { customStrategiesItem, customStrategies } = useSharedCustomStrategies(
    itemValue,
    itemChildren
  );

  const { setPreviousButton, closeModal } = useContext(ModalItemContext);
  const [itemToEdit, setItemToEdit] = useState();
  const [editOrCreateCustomStrategyItem, setEditOrCreateCustomStrategyItem] =
    useState();
  const [editOrCreateDCAStrategyItem, setEditOrCreateDCAStrategyItem] =
    useState();
  const dcaStrategiesItem = itemChildren.find(
    (child) => getName(child) === DCA_STRATEGIES_ITEM_NAME
  );
  const dcaStrategies = getChildren(dcaStrategiesItem);
  const customStrategyTemplate = getCreateItem(customStrategiesItem);
  const dcaStrategyTemplate = getCreateItem(dcaStrategiesItem);

  const [deleteCustomStrategy, { isLoading: deleteCustomStrategyLoading }] =
    sharedLib.sharedApi.configuration.useDeleteCustomStrategyMutation();

  const [itemToDelete, setItemToDelete] = useState();

  useEffect(() => {
    if (props.targets) {
      setEditOrCreateCustomStrategyItem(customStrategyTemplate);
    }
  }, [props.targets]);

  const handleSelectItem = (childItem, shouldCloseModal = true) => {
    customStrategies.forEach((child) => (child.chosen = false));
    dcaStrategies.forEach((child) => (child.chosen = false));
    const childType = getItemValueType(childItem);
    if (!childType) {
      // This is a newly created strategy
      childItem.chosen = true;
    }
    handleItemChange(childType ? parseInt(childType) : null);
    shouldCloseModal && closeModal();
  };

  const closeCreateOrEditScreen = () => {
    setEditOrCreateCustomStrategyItem(undefined);
    setEditOrCreateDCAStrategyItem(undefined);
  };

  const onSave = () => {
    closeCreateOrEditScreen();
    checkItemModified();
  };

  useEffect(() => {
    if (editOrCreateCustomStrategyItem || editOrCreateDCAStrategyItem) {
      setPreviousButton(
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={closeCreateOrEditScreen}
        />
      );
    } else {
      setPreviousButton(null);
    }
  }, [editOrCreateCustomStrategyItem, editOrCreateDCAStrategyItem]);

  const deleteStrategy = (itemToDelete, strategiesItem, strategies) => {
    const itemType = getItemValueType(itemToDelete);
    if (itemType) {
      itemToDelete.deleted = true;
    } else {
      strategiesItem["data"] = strategies.filter(
        (customStrategy) => itemToDelete !== customStrategy
      );
    }
  };

  const handleItemSelected = (itemToDelete) => {
    const isSelected = checkStrategyItemSelected(itemToDelete, itemValue);
    if (isSelected) {
      handleSelectItem(itemChildren[0], false);
    }
  };

  const handleDeleteStrategy = () => {
    displayResultMessage(
      deleteCustomStrategy({
        type: getItemValueType(itemToDelete),
        client_id: props.accountId,
      }).unwrap()
    ).then(() => {
      handleItemSelected(itemToDelete);
      deleteStrategy(itemToDelete, customStrategiesItem, customStrategies);
      setItemToDelete(undefined);
    });
  };

  const handleDeleteItem = (itemToDelete) => {
    if (props.useCustomStrategyEndpoints) {
      setItemToDelete(itemToDelete);
    } else {
      handleItemSelected(itemToDelete);
      const fieldType = getFieldType(itemToDelete);
      if (fieldType === STRATEGY_FIELD_TYPE_STRING) {
        deleteStrategy(itemToDelete, customStrategiesItem, customStrategies);
      } else if (fieldType === DCA_STRATEGY_FIELD_TYPE_STRING) {
        deleteStrategy(itemToDelete, dcaStrategiesItem, dcaStrategies);
      }
    }
    checkItemModified();
  };

  const checkItemSelected = (childItem) => {
    return (
      checkStrategyItemSelected(childItem, itemValue) &&
      !props?.strategyValuesModified
    );
  };

  const getAddStrategyButton = (headerItem, headerItemName) => {
    const name = headerItemName || getName(headerItem);
    let onClick;
    if (name === CUSTOM_STRATEGIES_ITEM_NAME) {
      onClick = () => setEditOrCreateCustomStrategyItem(customStrategyTemplate);
    } else if (name === DCA_STRATEGIES_ITEM_NAME) {
      onClick = () => setEditOrCreateDCAStrategyItem(dcaStrategyTemplate);
    }
    if (onClick) {
      return (
        <Link as="div" onClick={onClick}>
          <PlusOutlined style={{ marginRight: 4 }} />
          Add
        </Link>
      );
    }
  };

  const getEmptyDataText = (headerItem, headerItemName) => {
    const name = headerItemName || getName(headerItem);
    const getText = (isDCA) =>
      `You haven't created a ${isDCA ? "DCA" : "custom"} strategy yet`;
    if (name === CUSTOM_STRATEGIES_ITEM_NAME) {
      return getText(false);
    } else if (name === DCA_STRATEGIES_ITEM_NAME) {
      return getText(true);
    }
  };

  const validCustomStrategies = customStrategies.filter(
    (strategy) => !strategy.deleted
  );

  const items = props.onlyCustomStrategies
    ? validCustomStrategies
    : itemChildren;
  const customStrategiesExtraContent = items.length ? (
    <div style={{ marginTop: 24 }}>
      {getAddStrategyButton(undefined, CUSTOM_STRATEGIES_ITEM_NAME)}
    </div>
  ) : (
    <EmptyView
      iconKey={EmptyStatesType.Empty}
      iconWidth={100}
      style={{ padding: "36px 0" }}
      description={getEmptyDataText(undefined, CUSTOM_STRATEGIES_ITEM_NAME)}
      buttonText={t("custom_strategy.create")}
      isLinkButton={true}
      buttonOnClick={() =>
        setEditOrCreateCustomStrategyItem(customStrategyTemplate)
      }
    />
  );

  const defaultComponent = (
    <>
      {items.map((childItem, index) => {
        if (getIsSectionHeader(childItem)) {
          return (
            <HeaderItem
              key={index}
              item={childItem}
              action={getAddStrategyButton(childItem)}
              emptyDataText={getEmptyDataText(childItem)}
              {...props}
            />
          );
        }
        return <ConfigurationItem key={index} item={childItem} {...props} />;
      })}
      {props.onlyCustomStrategies && customStrategiesExtraContent}
    </>
  );

  const editOrCreateStrategyItem =
    editOrCreateCustomStrategyItem || editOrCreateDCAStrategyItem;
  const strategyOrderType =
    itemName.toLowerCase() === "entry"
      ? OrderTypes.ENTRY
      : OrderTypes.TAKE_PROFIT;

  const deleteStrategyWarningModal = (
    <Modal
      open={!!itemToDelete}
      title={t("custom_strategy.delete")}
      zIndex={1100}
      okText={t("labels.yes")}
      onOk={handleDeleteStrategy}
      okButtonProps={{ loading: deleteCustomStrategyLoading }}
      cancelText={t("labels.no")}
      onCancel={() => setItemToDelete(undefined)}
    >
      Are you sure you want to delete this strategy?
    </Modal>
  );

  return (
    <StrategyItemContext.Provider
      value={{
        checkItemSelected,
        handleSelectItem,
        handleDeleteItem,
        onSave,
        customStrategiesItem,
        dcaStrategiesItem,
        customStrategyTemplate,
        dcaStrategyTemplate,
        setEditOrCreateCustomStrategyItem,
        editOrCreateCustomStrategyItem,
        setEditOrCreateDCAStrategyItem,
        editOrCreateDCAStrategyItem,
        strategyOrderType,
        itemToEdit,
        setItemToEdit,
      }}
    >
      {editOrCreateStrategyItem ? (
        <ConfigurationItem item={editOrCreateStrategyItem} {...props} />
      ) : (
        defaultComponent
      )}
      {deleteStrategyWarningModal}
    </StrategyItemContext.Provider>
  );
};

export default DataRadioItem;
